import { useCallback, useEffect, useState } from 'react';

import { FreespinsApi, useApplicationState } from '@atlas/core';

import { isFreespinShownInProfileList, isKnownFreespinStatus } from 'common/freespinStatus';

const {
  FreeSpinsStatus: {
    ACTIVATED,
    ACTIVATION_READY,
    CANCELED,
    DISABLED,
    EXPIRED,
    PENDING_ACTIVATION,
    USED,
  },
} = FreespinsApi;

const STATUS_ORDER = {
  [ACTIVATION_READY]: 1,
  [PENDING_ACTIVATION]: 2,
  [ACTIVATED]: 3,
  [USED]: 4,
  [DISABLED]: 5,
  [CANCELED]: 6,
  [EXPIRED]: 7,
};

const DEFAULT_STATUSES = [ACTIVATED, USED, CANCELED, EXPIRED, ACTIVATION_READY, PENDING_ACTIVATION];

const useFreeSpins = ({ itemsPerPage = 100, statuses = DEFAULT_STATUSES } = {}) => {
  const { authenticated } = useApplicationState();

  const [freeSpins, setFreeSpins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const statusesKey = JSON.stringify(statuses);

  const refetch = useCallback(() => {
    setRefetchTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    if (!authenticated) {
      setLoading(false);
      return;
    }

    const options = {
      filter: {
        freeSpinStatuses: JSON.parse(statusesKey),
      },
      paging: {
        itemsPerPage,
      },
    };

    setLoading(true);
    setError(null);

    FreespinsApi.getFreeSpins(options)
      .then(({ freeSpins: items }) => {
        const knownOnly = items
          .filter(fs => isKnownFreespinStatus(fs.status))
          .filter(fs => isFreespinShownInProfileList(fs.status));
        const sortedFreeSpins = [...knownOnly].sort(
          (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status],
        );

        setFreeSpins(sortedFreeSpins);
      })
      .catch(setError)
      .finally(() => setLoading(false));
  }, [authenticated, itemsPerPage, statusesKey, refetchTrigger]);

  return { error, freeSpins, loading, refetch };
};

export default useFreeSpins;
